import React, { useCallback, useEffect, useState, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import gql from 'graphql-tag';
import { cloneDeep, merge, omit, isEmpty } from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Heading } from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import {
  ContentDeciderContainer,
  Generator,
  getTranslatedText,
  TableContainer,
  updateConditions,
  SkeletonLoading,
  useIsAuthorisedHook,
  useUserMSPCheckHook,
  SuperTable,
  getTableRowCount,
  saveTableRowCount,
  getCurrentUserHook,
  getAllColumnWidth,
  saveColumnWidth,
  getDefaultRowCount,
  EventEmitter,
  getDateOperators
} from '@nile/components';
import {
  TicketClient,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import {
  getAllTicketFields,
  TICKET_SCHEMA
} from '@nile/mangifera/src/Constants/Tickets';
import {
  getAccessor,
  getDependantField,
  getParentField,
  getTicketFields,
  getType,
  isClearable,
  isDependantField,
  isEditable,
  isFilterable,
  isSortable,
  getColumnWidth,
  getWidthFromPersonalization
} from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  checkDynamicQuery,
  fetchValues,
  generateOptions,
  getAllowedProperties,
  getAsyncOptions,
  getDefaultValue,
  getDynamicQuery,
  getKeyName,
  getMethodName,
  getVariables as getFilterQueryVariables
} from '@nile/mangifera/src/Pebbles/TicketToolbar';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import AllTicketsContext from '@nile/client_management/src/Containers/ClientDetails/AllTicketsTable/AllTicketsContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  DateTimeCell,
  SubjectCell,
  LinkCell,
  NameCell,
  PriorityCell,
  StepCell,
  TimerCell,
  ProgressCell,
  CustomTimeCell,
  CustomTextCell,
  CustomRadioCell,
  CustomCheckboxCell,
  CustomSelectCell,
  HoursMinutesCell
} from '@nile/mangifera/src/Components/Ticket';
import InlineEditTextCell from '@nile/mangifera/src/Components/Ticket/InlineEditTextCell';
import InlineStatusCell from '@nile/mangifera/src/Components/Ticket/InlineStatusCell';
import InlineEditDualCell from '@nile/mangifera/src/Components/Ticket/InlineEditDualCell';
import styled from 'styled-components';

const AllTicketsTable = (props) => {
  const flags = useFlags();
  const isMSP = useUserMSPCheckHook();

  const {
    intl,
    ticketViewMeta,
    entitiesMeta,
    defaultFilter,
    defaultPersonalisation,
    sortPersonalisation,
    savePersonalisation,
    allowEdit,
    noBulkSelect,
    extraOptions,
    tableName,
    columnPersonalisation,
    globalTabContext,
    columnsToRemove = [],
    propContext
  } = props;
  const dynamicPaginationFlag = flags?.dynamicPaginationTickets;
  const contextValue = useContext(AllTicketsContext);
  const context = propContext || contextValue;
  const tabContext = useContext(TicketContext);
  const user = getCurrentUserHook();
  const columnWidthObject = getAllColumnWidth(tableName);
  const optionFromProps = extraOptions || {};

  const pageSize = dynamicPaginationFlag
    ? getTableRowCount(tableName)
    : getDefaultRowCount(tableName);
  const [rowCount, setRowCount] = useState(pageSize);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const dataByRef = React.useRef(null);
  dataByRef.current = data;
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'Name', columns: [] }
  ]);
  const [listInfo, setListInfo] = useState({});
  const listInfoByRef = React.useRef(1);
  listInfoByRef.current = listInfo;

  const options = {
    pageSize,
    page: 1,
    sort: sortPersonalisation || [
      {
        attribute: 'createdTime',
        order: 'DESC'
      }
    ],
    condition: null,
    ...optionFromProps
  };

  const isTicketEditAuthorised = useIsAuthorisedHook('tickets.edit');

  const sortAttributes = ticketViewMeta?.sortAttributes;

  const defaultColumn = React.useMemo(() => {
    return {
      minWidth: 30,
      maxWidth: 500,
      width: 150,
      Priority: PriorityCell,
      Link: LinkCell,
      HTML: SubjectCell,
      Text: InlineEditTextCell,
      Timer: TimerCell,
      Status: InlineStatusCell,
      Step: StepCell,
      Name: NameCell,
      DateTime: DateTimeCell,
      Dependant: InlineEditTextCell,
      Dual: InlineEditDualCell,
      Progress: ProgressCell,
      CustomTime: CustomTimeCell,
      CustomText: CustomTextCell,
      CustomRadio: CustomRadioCell,
      CustomCheckbox: CustomCheckboxCell,
      CustomSelect: CustomSelectCell,
      HoursMinutes: HoursMinutesCell
    };
  }, []);

  const ticketAttributes =
    entitiesMeta?.find((item) => {
      return item.name === 'Ticket';
    })?.attributes || [];

  const getActions = useCallback((col) => {
    switch (col.name) {
      case 'subject':
        return {
          onClick: globalTabContext ? globalTabContext : tabContext.openTicket,
          updateMyData
        };
      default:
        return {
          save
        };
    }
  });

  const setConditions = (conditions) => {
    context.setViewConditions(conditions);
    fetchTickets(undefined, {
      page: listInfoByRef.current.page || options.page,
      pageSize: listInfoByRef.current.pageSize || options.pageSize,
      sort: listInfoByRef.current.sort || options.sort,
      condition: conditions,
      ...optionFromProps
    });
  };

  const updateAllConditions = (conditions, selectedField) => {
    updateConditions({
      condition: conditions,
      selectedField,
      viewConditions: listInfoByRef?.current?.condition,
      setConditions: setConditions
    });
  };

  const getVariables = (pageOptions) => {
    const variables = {
      listInfo: {
        value: pageOptions ? { ...pageOptions, ...optionFromProps } : options,
        type: 'ListInfoInput',
        required: true
      }
    };
    return variables;
  };

  const getCondition = (pageConditions) => {
    if (!defaultFilter && !pageConditions) return undefined;
    const operands = !isEmpty(defaultFilter?.operands)
      ? [...defaultFilter?.operands]
      : [];
    if (!isEmpty(pageConditions?.operands))
      operands.push(...pageConditions?.operands);
    return {
      joinOperator: defaultFilter?.joinOperator || pageConditions?.joinOperator,
      operands
    };
  };

  // API CALL FOR COLUMN PERSONALIZATION
  const getColumns = async () => {
    const response = await AccountClient.query({
      query: GET_PERSONALIZATION_BY_NAME,
      fetchPolicy: 'network-only',
      variables: {
        userId: user.userId,
        category: 'AllTicketTableColumnPersonalization',
        name: tableName
      }
    });

    let columns = null;

    if (response?.data?.getPersonalizationByCategoryAndName?.value) {
      columns = JSON.parse(
        response?.data?.getPersonalizationByCategoryAndName?.value
      ).columns;
      columns = columns.map((el) => {
        return {
          ...el,
          label: getTranslatedText(intl, el.label)
        };
      });
    } else {
      columns = defaultPersonalisation;
    }

    return columns;
  };

  const fetchTickets = async (personalization, pageOptions) => {
    setLoading(true);
    let personalizedColumns = defaultPersonalisation;
    if (columnPersonalisation) {
      personalizedColumns = await getColumns();
    }

    // const personalizedColumns = allPersonalizedColumns.filter(
    //   ({ name }) => name !== 'client'
    // );

    const generator = new Generator({
      operation: 'getTicketList',
      variables: getVariables(pageOptions),
      fields: getTicketFields(personalizedColumns)
    });
    const queryString = generator.queryBuilder();
    const response = await TicketClient.query({
      query: gql`
        ${queryString.query}
      `,
      fetchPolicy: 'network-only',
      variables: {
        listInfo: pageOptions
          ? {
              page:
                pageOptions.page || listInfoByRef.current.page || options.page,
              pageSize,
              sort: pageOptions.sort,
              condition: getCondition(
                pageOptions.condition || context.viewConditions || null
              ),
              ...optionFromProps
            }
          : {
              page: 1,
              pageSize: pageSize,
              condition: getCondition(null),
              sort: listInfo.sort || [
                {
                  attribute: 'createdTime',
                  order: 'DESC'
                }
              ],
              ...optionFromProps
            }
      }
    });

    const allowedProperties = getAllowedProperties(entitiesMeta, intl);
    const filteredCols = [];
    personalizedColumns.map((el) => {
      ticketAttributes.forEach((c) => {
        const shouldColumnBeRemoved = columnsToRemove.includes(el.name);
        if (el.name === c.name && !shouldColumnBeRemoved) {
          filteredCols.push({
            ...c,
            label: getTranslatedText(intl, c.label),
            filterType: c.filterType
          });
        }
      });
    });

    const columns = filteredCols.map((col, idx) => {
      return {
        Header: col.label,
        header: col.label,
        accessor: getAccessor(col),
        accessorKey: getAccessor(col),
        minWidth: getColumnWidth(col.name).minWidth,
        minSize: getColumnWidth(col.name).minWidth,
        width: getColumnWidth(col.name).width,
        size: getWidthFromPersonalization(col.name, columnWidthObject),
        type: getType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: !isSortable(col, sortAttributes),
        editable: allowEdit && isTicketEditAuthorised && isEditable(col),
        clearable: allowEdit && isTicketEditAuthorised && isClearable(col),
        disableFilters: !isFilterable(allowedProperties, col),
        helpers: {
          getDefaultValue,
          getDynamicQuery,
          checkDynamicQuery,
          generateOptions,
          getMethodName,
          getKeyName,
          getDateOperators,
          fetchValues,
          getAsyncOptions,
          updateConditions: updateAllConditions,
          getVariables: getFilterQueryVariables,
          ...(props.overrideHelpers || {})
        },
        actions: getActions(col),
        field: {
          ...col,
          parent: ticketAttributes?.find((attr) => attr?.name === col?.nestedTo)
        },
        meta: {
          ticketAttributes
        },
        parentField: isDependantField(col.name)
          ? {
              ...ticketAttributes.find(
                (el) => el.name === getParentField(col.name)
              ),
              label: getTranslatedText(
                intl,
                ticketAttributes.find(
                  (el) => el.name === getParentField(col.name)
                ).label
              )
            }
          : col,
        dependantField: isDependantField(col.name)
          ? col
          : ticketAttributes.find(
              (el) => el.name === getDependantField(col.name)
            )
      };
    });
    setLoading(false);
    setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
    setData(response?.data?.getTicketList?.tickets);
    setListInfo(response?.data?.getTicketList?.listInfo);
  };

  useEffect(() => {
    return () => {
      if (savePersonalisation) savePersonalisation(listInfoByRef.current?.sort);
    };
  }, []);

  useEffect(() => {
    fetchTickets(undefined, options);
  }, []);

  useEffect(() => {
    EventEmitter.subscribe('refreshAllTableList', fetchTickets);
    return function cleanup() {
      EventEmitter.unsubscribe('refreshAllTableList');
    };
  });

  // Reset filter
  useEffect(() => {
    if (!context.viewConditions) {
      fetchTickets(undefined, {
        page: listInfoByRef.current.page || options.page,
        pageSize: listInfoByRef.current.pageSize || options.pageSize,
        sort: listInfoByRef.current.sort || options.sort,
        condition: null,
        ...optionFromProps
      });
    }
  }, [context.viewConditions]);

  const updateMyData = (newTicketData, diff) => {
    const newTableData = cloneDeep(dataByRef.current);
    const updatedTicketIndex = dataByRef.current.findIndex(
      (el) => el.ticketId === newTicketData.ticketId
    );
    const updatedDiff = omit(diff, ['id']);
    if (diff.client && newTicketData?.site) {
      updatedDiff.site = newTicketData.site;
    }
    newTableData[updatedTicketIndex] = merge(
      newTableData[updatedTicketIndex],
      updatedDiff
    );
    setData(newTableData);
  };

  const getRequiredVariables = (diff) => {
    const variables = {};
    for (const prop in diff) {
      variables[prop] = {
        value: diff[prop],
        type: TICKET_SCHEMA[prop],
        inputVariable: prop !== 'id',
        required: prop === 'id'
      };
    }
    return variables;
  };

  const save = (updatedProperties, ticket, oldData, callback) => {
    const diff = { ...updatedProperties };
    if (isEmpty(diff)) {
      if (callback) callback('success');
      return;
    }
    if (ticket.ticketId) {
      diff.id = ticket.ticketId;
    }
    const generator = new Generator({
      operation: 'updateTicket',
      variables: getRequiredVariables(diff),
      fields: getAllTicketFields()
    });
    const mutationString = generator.mutationBuilder();

    TicketClient.mutate({
      mutation: gql`
        ${mutationString.query}
      `,
      variables: diff
    })
      .then((res) => {
        if (res?.data?.updateTicket) {
          updateMyData(res?.data?.updateTicket, diff, oldData);
          if (callback) callback('success');
        } else {
          if (callback) callback('error');
        }
      })
      .catch((err) => {
        if (callback) callback('error');
      });
  };

  return (
    <TableContainer>
      <ContentDeciderContainer
        isLoading={loading}
        hasContent
        fullHeight
        loadingContent={
          <div>
            <SkeletonLoading hideStepNav hideSecondaryHeader hideHeader />
          </div>
        }
        mainContent={
          data?.length > 0 ? (
            <SuperTable
              context={context}
              columns={columnGroup}
              data={data}
              setData={setData}
              rowCount={rowCount}
              updateMyData={updateMyData}
              refreshTable={fetchTickets}
              listInfo={listInfo}
              pagination={
                Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1
              }
              pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
              recordCount={listInfo.totalCount}
              activePage={listInfo.page || 0}
              isLoading={loading}
              noBulkSelect={noBulkSelect}
              cardTable
              user={user}
              dynamicRowCount={dynamicPaginationFlag}
              saveTableRowCount={saveTableRowCount}
              columnWidthPersonalization
              saveColumnWidth={saveColumnWidth}
              defaultColumnCells={defaultColumn}
              padded={props?.padded}
              overscan={rowCount}
            />
          ) : (
            <div className="empty-state flex center jcenter pt40">
              <Heading element="h2">
                <FormattedMessage id="asset.details.tickets.empty" />
              </Heading>
              <div>
                <img
                  className="empty-state-img ml20 h180 w180"
                  src={`${envConfig.imageRoot}svg/TicketEmptyState.svg`}
                  alt="loading"
                />
              </div>
            </div>
          )
        }
      />
    </TableContainer>
  );
};

export default injectIntl(withRouter(AllTicketsTable));
